import capitalize from 'lodash/capitalize';

import { Icons, StatusChipV2, Tooltip } from '@cast/design-system';
import {
  STATUS_DISPLAY_NAME_MAP,
  getPodAppliedStatus,
  type PodAppliedStatus,
} from '@cast/split-me/features/cluster/workload-optimization';
import {
  OptimizationWorkloadPod,
  WorkloadRecommendationStatusTypeEnum,
} from '@cast/types';

type Props = {
  pod: OptimizationWorkloadPod;
  // True when the parent workload has recommendationStatus.type === STATUS_UNKNOWN
  // (vertical autoscaling disabled). When true, the pod must render as
  // notApplied regardless of its isLatestRecommendation flag.
  isOptimizationDisabled?: boolean;
};

const NotAppliedGlyph = () => (
  <StatusChipV2
    label="—"
    color="neutral"
    variant="subtle"
    size="medium"
    testId="pod-list-status-not-applied"
  />
);

const ApplyingChip = () => (
  <StatusChipV2
    label="Applying"
    color="success"
    variant="subtle"
    size="medium"
    loading
    testId="pod-list-status-applying"
  />
);

const AppliedChip = () => (
  <StatusChipV2
    label={
      STATUS_DISPLAY_NAME_MAP[WorkloadRecommendationStatusTypeEnum.APPLIED]
    }
    color="success"
    variant="subtle"
    size="medium"
    testId="pod-list-status-applied"
  />
);

const renderAppliedStatus = (status: PodAppliedStatus) => {
  if (status === 'applied') {
    return <AppliedChip />;
  }
  if (status === 'pending') {
    return <ApplyingChip />;
  }
  return <NotAppliedGlyph />;
};

/** Meta Pod list status: Error / Warning / Applying / Applied / Not applied. */
export const PodListStatusCell = ({ pod, isOptimizationDisabled }: Props) => {
  const { recommendation, inPlaceResize, status } = pod;

  const isError =
    status === 'FAILED' ||
    inPlaceResize?.status === 'ERROR' ||
    !!inPlaceResize?.error;

  if (isError) {
    const errorChip = (
      <StatusChipV2
        testId="pod-list-status-error"
        color="error"
        variant="subtle"
        size="medium"
        slots={{ icon: <Icons.Warning /> }}
      />
    );

    const errorMessage = inPlaceResize?.error;
    if (errorMessage) {
      return (
        <Tooltip title={capitalize(errorMessage)} placement="top" arrow>
          {errorChip}
        </Tooltip>
      );
    }

    return errorChip;
  }

  if (recommendation?.resourcesMismatched) {
    return (
      <StatusChipV2
        testId="pod-list-status-warning"
        color="warning"
        variant="subtle"
        size="medium"
        slots={{ icon: <Icons.WarningCircle /> }}
      />
    );
  }

  // RESIZING is a separate in-flight state orthogonal to the recommendation
  // tri-state; preserve existing UX.
  if (inPlaceResize?.status === 'RESIZING') {
    return <ApplyingChip />;
  }

  return renderAppliedStatus(getPodAppliedStatus(pod, isOptimizationDisabled));
};
